import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../common/Modal";
import Button from "../common/Button";
import CompanyDetailsForm from "./CompanyDetailsForm";
import { getCompanyDetailsThunk } from "../../store/thunks/companyDetailsThunks";
import CompanySeoModal from "./CompanySeoModal";

const CompanyDetailsModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isSeoModalOpen, setIsSeoModalOpen] = useState(false);
  const { companyDetails, loading, error } = useSelector(
    (state) => state.companyDetails
  );

  useEffect(() => {
    if (isOpen) {
      dispatch(getCompanyDetailsThunk());
    }
  }, [isOpen, dispatch]);

  const handleEditClick = () => setIsEditing(true);
  const handleFormClose = () => setIsEditing(false);
  const handleSeoModalOpen = () => setIsSeoModalOpen(true);
  const handleSeoModalClose = () => setIsSeoModalOpen(false);

  const renderSection = (title, content) => {
    if (!content) return null;
    return (
      <section className="mb-8">
        <h3 className="text-2xl font-bold mb-4 border-b pb-2">{title}</h3>
        {content}
      </section>
    );
  };

  const renderAddress = (address) => {
    if (!address) return <p className="text-neutral-dark">Not Provided</p>;
    return (
      <p>
        {address.line1}
        {address.line2 && `, ${address.line2}`}
        <br />
        {address.city}, {address.state} - {address.pinCode}
      </p>
    );
  };

  const renderSocialLinks = (links) => {
    if (!links) return <p className="text-neutral-dark">Not Provided</p>;
    const platforms = Object.entries(links).filter(([_, value]) => value);
    if (!platforms.length)
      return <p className="text-neutral-dark">Not Provided</p>;

    return (
      <div className="flex space-x-6">
        {platforms.map(([platform, url]) => (
          <a
            key={platform}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={platform}
            className="text-primary hover:text-primary-dark transition duration-200"
          >
            <i className={`fab fa-${platform} text-3xl`}></i>
          </a>
        ))}
      </div>
    );
  };

  const renderContactPerson = (contactPerson) => {
    if (!contactPerson)
      return <p className="text-neutral-dark">Not Provided</p>;
    const { name, email, phone } = contactPerson;

    return (
      <div className="space-y-2">
        <p>
          <strong>Name:</strong> {name || "Not Provided"}
        </p>
        <p>
          <strong>Email:</strong>{" "}
          {email ? (
            <a
              href={`mailto:${email}`}
              className="text-primary hover:underline"
            >
              {email}
            </a>
          ) : (
            "Not Provided"
          )}
        </p>
        <p>
          <strong>Phone:</strong> {phone || "Not Provided"}
        </p>
      </div>
    );
  };

  const renderLogo = (logoUrl) => {
    if (!logoUrl)
      return <p className="text-neutral-dark text-center">No logo uploaded.</p>;

    return (
      <div className="flex justify-center items-center mb-8">
        <img
          src={logoUrl}
          alt="Company Logo"
          className="max-w-full h-auto object-contain rounded shadow-lg"
          style={{ maxHeight: "200px" }}
        />
      </div>
    );
  };

  return (
    <>
      <>
        <div className="w-full p-4">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <span
                className="loader"
                aria-label="Loading company details..."
              ></span>
            </div>
          ) : error ? (
            <p className="text-danger text-center">
              Failed to load company details.
            </p>
          ) : companyDetails ? (
            <div className="space-y-8">
              {renderLogo(companyDetails.logoUrl)}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {renderSection(
                  "Basic Info",
                  <div className="space-y-4">
                    <p>
                      <strong>Name:</strong>{" "}
                      {companyDetails.companyName || "Not Provided"}
                    </p>
                    <p>
                      <strong>Website:</strong>{" "}
                      {companyDetails.website ? (
                        <a
                          href={companyDetails.website}
                          target="_blank"
                          className="text-primary hover:underline"
                          rel="noopener noreferrer"
                        >
                          {companyDetails.website}
                        </a>
                      ) : (
                        "Not Provided"
                      )}
                    </p>
                    <p>
                      <strong>Tax ID:</strong>{" "}
                      {companyDetails.taxId || "Not Provided"}
                    </p>
                    <p>
                      <strong>Brand Color:</strong>{" "}
                      {companyDetails.brandColor || "Not Provided"}
                      {companyDetails.brandColor && (
                        <span
                          className="inline-block ml-2 w-6 h-6 border rounded"
                          style={{ backgroundColor: companyDetails.brandColor }}
                        />
                      )}
                    </p>
                  </div>
                )}
                {renderSection(
                  "Address",
                  renderAddress(companyDetails.address)
                )}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {renderSection(
                  "Social Media",
                  renderSocialLinks(companyDetails.socialMediaLinks)
                )}
                {renderSection(
                  "Contact Person",
                  renderContactPerson(companyDetails.contactPerson)
                )}
              </div>
              <div className="flex justify-end space-x-4 mt-4">
                <Button
                  variant="secondary-outline"
                  onClick={handleSeoModalOpen}
                >
                  Edit SEO
                </Button>
                <Button variant="primary" onClick={handleEditClick}>
                  Edit Company Details
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-64">
              <Button variant="primary" onClick={handleEditClick}>
                Add Company Details
              </Button>
            </div>
          )}
        </div>
      </>

      {isEditing && (
        <CompanyDetailsForm
          isOpen={isEditing}
          onClose={handleFormClose}
          initialData={companyDetails}
        />
      )}

      <CompanySeoModal isOpen={isSeoModalOpen} onClose={handleSeoModalClose} />
    </>
  );
};

export default CompanyDetailsModal;
